import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import useStripeLoader, { clearStripeCache } from '../hooks/useStripeLoader';
import Checkout from './Checkout';
import Loading from './shared/Loading';
import { STRIPE_AD_BLOCKER_ERROR, STRIPE_CONNECTION_ERROR } from '../utils/constants';

/**
 * Wrapper component that provides Stripe loading functionality to the Checkout component.
 * Uses the useStripeLoader hook to handle lazy loading of Stripe.js with retry logic
 * and ad-blocker detection.
 *
 * This wrapper passes Stripe-related props to the Checkout component:
 * - stripe: The loaded Stripe instance (null if not loaded)
 * - stripeLoading: Boolean indicating if Stripe is currently loading
 * - stripeError: Error object if Stripe failed to load (with isAdBlocker flag)
 * - loadStripe: Function to trigger Stripe loading on-demand
 *
 * @param {object} props - All props are forwarded to the Checkout component
 * @returns {JSX.Element} Checkout component with Stripe loading capabilities
 */
function CheckoutWithStripe(props) {
    const {
        stripe,
        loading: stripeLoading,
        error: stripeError,
        loadStripe,
    } = useStripeLoader({ loadOnMount: true });

    /**
     * Handle retry action for Stripe loading errors
     * Clears the Stripe cache before attempting to reload
     */
    const handleRetry = () => {
        clearStripeCache();
        loadStripe();
    };

    if (stripeLoading) {
        return <Loading />;
    }

    // Show error state with retry option if Stripe fails to load
    if (stripeError) {
        const isAdBlockerError = Boolean(stripeError.isAdBlocker);
        const errorTitle = isAdBlockerError ? 'Ad blocker detected' : 'Connection error';
        const errorMessage = isAdBlockerError ? STRIPE_AD_BLOCKER_ERROR : STRIPE_CONNECTION_ERROR;
        const supportMessage = 'If the problem persists, contact support@ghost.org.';

        return (
            <div className="checkout-warning">
                <div className="checkout-warning-message">
                    <h1 className="modal-title adblocker-title">{errorTitle}</h1>
                    <div className="adblocker-warning">
                        <p>{errorMessage}</p>
                        <p>{supportMessage}</p>
                    </div>
                    <button
                        className="gh-btn gh-btn-black gh-btn-block"
                        onClick={handleRetry}
                        type="button"
                    >
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    // Stripe is ready - render Checkout with guaranteed Stripe instance
    return <Checkout {...props} stripe={stripe} />;
}

CheckoutWithStripe.propTypes = {
    // All props are forwarded to Checkout, so we inherit its PropTypes
    // Router props are provided by withRouter HOC
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
        replace: PropTypes.func.isRequired,
    }).isRequired,
    updateTitle: PropTypes.func.isRequired,
    showNav: PropTypes.func.isRequired,
    match: PropTypes.shape({
        params: PropTypes.shape({
            id: PropTypes.string,
        }),
    }),
};

export default withRouter(CheckoutWithStripe);
